import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { JournalistFilter, JournalistFormData, JournalistsState } from '../../../types/journalist.types';
import apiClient from '../../../api/apiClient';
import { API_ENDPOINTS } from 'api/endPoints';

// Initial state
const initialState: JournalistsState = {
  items: [],
  filteredItems: [],
  isLoading: false,
  error: null,
  filters: {
    publisherPK: '',
    searchTerm: '',
    specialty: '',
  },
  pagination: {
    page: 0,
    rowsPerPage: 10,
    totalCount: 0,
  },
};

// Async thunks
export const fetchJournalists = createAsyncThunk('journalists/fetchJournalists', async () => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.JOURNALISTS.LIST);
  return response.data;
});

export const fetchFilteredJournalists = createAsyncThunk(
  'journalists/fetchFilteredJournalists',
  async (filters: JournalistFilter) => {
    const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.JOURNALISTS.FILTER, {
      params: filters,
    });
    return response.data;
  }
);

export const fetchJournalistsByPublisher = createAsyncThunk(
  'journalists/fetchJournalistsByPublisher',
  async (publisherId: string) => {
    const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.JOURNALISTS.BY_PUBLISHER(publisherId));
    return response.data;
  }
);

export const createJournalist = createAsyncThunk(
  'journalists/createJournalist',
  async (journalistData: JournalistFormData) => {
    const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.JOURNALISTS.CREATE, journalistData);
    return response.data;
  }
);

export const updateJournalist = createAsyncThunk(
  'journalists/updateJournalist',
  async ({ id, data }: { id: string; data: JournalistFormData }) => {
    const response = await apiClient.put(API_ENDPOINTS.PUBLISHER.JOURNALISTS.UPDATE(id), data);
    return response.data;
  }
);

export const deleteJournalist = createAsyncThunk(
  'journalists/deleteJournalist',
  async (id: string) => {
    await apiClient.delete(API_ENDPOINTS.PUBLISHER.JOURNALISTS.DELETE(id));
    return id;
  }
);

// Slice
const journalistsSlice = createSlice({
  name: 'journalists',
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<{ key: keyof JournalistFilter; value: string }>) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
      
      // Reset pagination
      state.pagination.page = 0;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.pagination.rowsPerPage = action.payload;
      state.pagination.page = 0;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all journalists
      .addCase(fetchJournalists.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchJournalists.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchJournalists.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch journalists';
      })
      
      // Fetch filtered journalists
      .addCase(fetchFilteredJournalists.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFilteredJournalists.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchFilteredJournalists.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch filtered journalists';
      })
      
      // Fetch journalists by publisher
      .addCase(fetchJournalistsByPublisher.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchJournalistsByPublisher.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchJournalistsByPublisher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch journalists by publisher';
      })
      
      // Create journalist
      .addCase(createJournalist.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createJournalist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items.push(action.payload);
        state.filteredItems.push(action.payload);
        state.pagination.totalCount = state.filteredItems.length;
      })
      .addCase(createJournalist.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create journalist';
      })
      
      // Update journalist
      .addCase(updateJournalist.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateJournalist.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedJournalist = action.payload;
        
        // Update in items array
        const itemIndex = state.items.findIndex(item => item.id === updatedJournalist.id);
        if (itemIndex !== -1) {
          state.items[itemIndex] = updatedJournalist;
        }
        
        // Update in filtered items array
        const filteredIndex = state.filteredItems.findIndex(item => item.id === updatedJournalist.id);
        if (filteredIndex !== -1) {
          state.filteredItems[filteredIndex] = updatedJournalist;
        }
      })
      .addCase(updateJournalist.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update journalist';
      })
      
      // Delete journalist
      .addCase(deleteJournalist.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteJournalist.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedId = action.payload;
        
        // Remove from items array
        state.items = state.items.filter(item => item.id !== deletedId);
        
        // Remove from filtered items array
        state.filteredItems = state.filteredItems.filter(item => item.id !== deletedId);
        
        state.pagination.totalCount = state.filteredItems.length;
      })
      .addCase(deleteJournalist.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to delete journalist';
      });
  },
});

export const { setFilter, setPage, setRowsPerPage, resetFilters } = journalistsSlice.actions;

export default journalistsSlice.reducer;